import morph from "../helpers/morph";
import "./mini-cart-slider";

const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.requestId = 1;
    this.closeButton = this.querySelector("[data-close]");
    this.content = this.querySelector(".mini-cart__content");
    this.wrapper = this.querySelector("[data-wrapper]");
    this.itemCount = Number(this.dataset.itemCount);
    this.header = this.closest("body").querySelector("header");
    this.queuedQuantityUpdates = new Map();
    this.quantityUpdateInFlight = false;
    this.quantityUpdateVersion = 0;

    this.closeButton?.addEventListener("click", () => this._close());
    this.addEventListener("click", (e) => {
      if (e.target.closest(".mini-cart__content")) return;

      this._close();
    });

    this.addEventListener("click", (e) => {
      const removeButton = e.target.closest("[data-remove]");

      if (!removeButton) return;

      this._updateQty({ key: removeButton.dataset.remove, quantity: 0 });
    });

    this.handleDocumentClick = (e) => {
      // Ignore clicks on items no longer in DOM
      if (!document.body.contains(e.target)) return;

      if (!this.content.contains(e.target)) {
        this._close();
      }
    };

    this.closeEsc = (e) => {
      if (e.key === "Escape") {
        this._close();
      }
    };

    this.handleOpen = () => this._open();
    this.handleClose = () => this._close();
    this.handleUpdateCart = (e) => {
      const requestId = e.detail.data?.requestId;

      // Debounce - cancel update if another request is already in progress.
      if (requestId === this.requestId || !requestId) {
        const sections = e.detail.cart?.sections;
        const miniCartMarkup = sections && sections["mini-cart"];

        // If the sections markup isn't present (e.g. some cart endpoints
        // don't return rendered sections), fall back to a full refresh.
        if (!miniCartMarkup) {
          this._refreshCart();
          return;
        }

        this._updateCart(miniCartMarkup);
      }
    };

    this.addEventListener("change", (e) => {
      const item = e.target.closest("[data-item-key]");
      if (!item) return;

      this._updateQty({
        key: item.dataset.itemKey,
        quantity: parseInt(e.target.value, 10),
      });
    });

    this.addEventListener("quantity-selector:update", (e) => {
      this._updateQty({
        key: e.detail.key,
        quantity: parseInt(e.detail.quantity, 10),
      });
    });

    if (new URL(window.location).searchParams.has("cart")) {
      this._open();
    }
  }

  connectedCallback() {
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.closeEsc);
    document.addEventListener("mini-cart:open", this.handleOpen);
    document.addEventListener("mini-cart:close", this.handleClose);
    document.addEventListener("updateCart", this.handleUpdateCart);

    this.onResize = debounce(() => {
      this._setPos();
    }, 200);
    window.addEventListener("resize", this.onResize);
    this.onPageShow = () => {
      if (this.hasAttribute("open")) this._setPos();
    };
    window.addEventListener("pageshow", this.onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.closeEsc);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("mini-cart:open", this.handleOpen);
    document.removeEventListener("mini-cart:close", this.handleClose);
    document.removeEventListener("updateCart", this.handleUpdateCart);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("pageshow", this.onPageShow);
    window.clearTimeout(this.positionTimer);
  }

  _toggle() {
    this.hasAttribute("open") ? this._close() : this._open();
  }

  _open() {
    if (this.hasAttribute("open")) return;

    document.body.classList.add("mini-cart-open");

    this.style.display = "block";
    window.requestAnimationFrame(() => {
      this._setPos();
      this.setAttribute("open", "");
      window.lockScroll(this);
      this.returnFocusEl = document.activeElement;
      window.trapFocus(this, this.closeButton);
      window.clearTimeout(this.positionTimer);
      this.positionTimer = window.setTimeout(() => this._setPos(), 350);
    });
  }

  _close() {
    if (!this.hasAttribute("open")) return;

    this.removeAttribute("open");

    document.body.classList.remove("mini-cart-open");

    const onEnd = () => {
      this.removeEventListener("transitionend", onEnd);

      if (this.hasAttribute("open")) return;
      this.style.display = null;
      window.removeTrapFocus(this.returnFocusEl);
      window.unlockScroll(this);
    };
    this.addEventListener("transitionend", onEnd);
  }

  _setPos() {
    if (!this.header || !this.wrapper) return;

    const headerPaddingBottom =
      parseInt(window.getComputedStyle(this.header).paddingBottom, 10) || 0;
    const headerBottom =
      this.header.getBoundingClientRect().bottom - headerPaddingBottom;
    const visibleHeaderBottom = Math.max(
      0,
      Math.min(headerBottom, Math.max(0, window.innerHeight - 1)),
    );

    this.wrapper.style.top = `${visibleHeaderBottom}px`;
  }

  _updateQty({ key, quantity }) {
    if (!key || !Number.isInteger(quantity) || quantity < 0) return;

    const version = ++this.quantityUpdateVersion;
    this.queuedQuantityUpdates.set(key, { quantity, version });
    this._flushQtyQueue();
  }

  _flushQtyQueue() {
    if (this.quantityUpdateInFlight || !this.queuedQuantityUpdates.size) return;

    const [key, update] = this.queuedQuantityUpdates.entries().next().value;
    const { quantity, version } = update;
    this.queuedQuantityUpdates.delete(key);
    this.quantityUpdateInFlight = true;

    const request = window.changeCart(
      { id: key, quantity },
      {
        onError: () => this._refreshCart(version),
        data: { requestId: ++this.requestId },
        onFinally: () => {
          this.quantityUpdateInFlight = false;
          this._flushQtyQueue();
        },
      },
    );
    request.catch(() => {});
  }

  _refreshCart(version = this.quantityUpdateVersion) {
    return window
      .fetchPageData({
        path: window.location.pathname || "/",
        options: { sectionId: "mini-cart" },
      })
      .then((markup) => {
        if (version !== this.quantityUpdateVersion) return;
        this._updateCart(markup);
      });
  }

  _updateCart(markup) {
    const html = new DOMParser().parseFromString(markup, "text/html");
    const sourceContent = html.querySelector(".mini-cart__content");

    morph(this.content, sourceContent, {
      onNodeAdded: (node) => {
        if (node.nodeName === "SCRIPT") {
          var script = document.createElement("script");
          [...node.attributes].forEach((attr) => {
            script.setAttribute(attr.nodeName, attr.nodeValue);
          });

          script.innerHTML = node.innerHTML;
          node.replaceWith(script);
        }
      },
      onBeforeElUpdated: (fromEl, toEl) => {
        if (fromEl.nodeName === "SCRIPT" && toEl.nodeName === "SCRIPT") {
          var script = document.createElement("script");
          [...toEl.attributes].forEach((attr) => {
            script.setAttribute(attr.nodeName, attr.nodeValue);
          });

          script.innerHTML = toEl.innerHTML;
          fromEl.replaceWith(script);
          return false;
        }

        if (fromEl.nodeName === "IMG" && toEl.nodeName === "IMG") {
          if (fromEl.srcset !== toEl.srcset) {
            fromEl.replaceWith(toEl);
            return false;
          }
        }

        if ("forceUpdate" in fromEl.dataset || "forceUpdate" in toEl.dataset) {
          fromEl.replaceWith(toEl);
          return false;
        }

        if ("attributesOnly" in fromEl.dataset) {
          fromEl.dataset.attributesOnly.split(",").forEach((attribute) => {
            // skip blanks
            if (!attribute) return;

            fromEl.setAttribute(attribute, toEl.getAttribute(attribute));
          });

          return false;
        }
      },
    });
  }
}

if (!customElements.get("mini-cart")) {
  customElements.define("mini-cart", MiniCart);
}
